import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { POI_SCENE_MAP, getSceneKeyByLabel, DEFAULT_SCENE_KEY } from '../../../../config/poiSceneConfig';

interface PoiData {
  gid: number;
  name: string;
  addr: string;
  lon: string;
  lat: string;
  city: string;
  cou: string;
  str: string;
  pro: string;
  poiScene?: string;
  poi_scene?: string;
  [key: string]: any;
}

interface BuildingInfoListProps {
  pois: PoiData[];
  onPoiClick?: (poi: PoiData, index: number) => void;
  visible: boolean;
  onClose?: () => void;
  layerControlCollapsed?: boolean;
  scenePanelCollapsed?: boolean;
  sceneDetailVisible?: boolean;
}

/** 带 -50 前缀的标签样式（浅色背景用于标签气泡） */
const getSceneTagStyle = (label?: string) => {
  const key = getSceneKeyByLabel(label);
  const def = POI_SCENE_MAP[key] ?? POI_SCENE_MAP[DEFAULT_SCENE_KEY];
  const bgLight = def.bg.replace('-500', '-50');
  return {
    bg: bgLight,
    text: def.text,
    border: def.border,
  };
};

// 读取 poiScene 字段（兼容 API 下划线命名 poi_scene）
const getPoiScene = (poi: PoiData): string => poi.poiScene || poi.poi_scene || '';

// 解析 poiScene 为标签数组
const parseTags = (poiScene?: string): string[] => {
  if (!poiScene) return [];
  return poiScene.split('、').filter(t => t.trim());
};

// 获取标签样式（用于渲染）
const getTagStyleClass = (tag: string): string => {
  const style = getSceneTagStyle(tag);
  return `${style.bg} ${style.text}`;
};

/** 获取 POI 场景主色（序号圆点填充用） */
const getNumberColor = (firstTag: string | undefined): string => {
  const key = getSceneKeyByLabel(firstTag ?? '');
  const def = POI_SCENE_MAP[key] ?? POI_SCENE_MAP[DEFAULT_SCENE_KEY];
  return def.color;
};

const BuildingInfoList: React.FC<BuildingInfoListProps> = ({ 
  pois, 
  onPoiClick,
  visible,
  onClose,
  layerControlCollapsed = false,
  scenePanelCollapsed = false,
  sceneDetailVisible = false,
}) => {
  const [searchText, setSearchText] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // 过滤后的 POI 列表
  const filteredPois = useMemo(() => {
    if (!searchText.trim()) return pois;
    const q = searchText.trim().toLowerCase();
    return pois.filter(poi => {
      const name = (poi.name || '').toLowerCase();
      const scene = getPoiScene(poi).toLowerCase();
      return name.includes(q) || scene.includes(q);
    });
  }, [pois, searchText]);

  useLayoutEffect(() => {
    if (visible && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [visible]);

  if (!visible || pois.length === 0) {
    return null;
  }

  const sceneLeft = layerControlCollapsed ? 62 : 190;
  const sceneWidth = scenePanelCollapsed ? 36 : 380;
  const panelLeft = sceneDetailVisible ? sceneLeft + sceneWidth + 10 : sceneLeft;
  const scenePanelRight = sceneDetailVisible ? sceneLeft + sceneWidth : sceneLeft;

  if (collapsed) {
    return (
      <div
        className="absolute top-36 z-[30]"
        style={{ width: 36, left: scenePanelRight + 10 }}
      >
        <div
          className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setCollapsed(false)}
        >
          <div className="flex flex-col items-center py-3 gap-2">
            <svg className="w-4 h-4 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span className="text-[10px] text-slate-500 font-medium whitespace-nowrap [writing-mode:vertical-rl]">
              POI列表
            </span>
            <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-1 rounded">{pois.length}</span>
            <i className="ri-arrow-right-s-line text-slate-400 text-sm" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute top-36 z-[30]"
      style={{ width: 380, left: panelLeft }}
    >
      <div className="bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
        {/* === 头部 === */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <h3 className="text-sm font-semibold text-slate-700">POI 列表</h3>
            <span className="text-[11px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full font-medium">
              {pois.length}
            </span>
          </div>
          {onClose && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCollapsed(true)}
                className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer"
                title="折叠"
              >
                <i className="ri-subtract-line text-xs" />
              </button>
              <button
                onClick={onClose}
                className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          )}
        </div>

        {/* === 搜索栏 === */}
        <div className="px-4 pt-3 pb-2 border-b border-slate-100">
          <div className="relative">
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
              <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              ref={inputRef}
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="搜索名称、地址、类别..."
              className="w-full pl-8 pr-8 py-1.5 text-xs rounded-lg border border-slate-200 bg-slate-50 
                         focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 
                         outline-none transition-all placeholder:text-slate-400"
            />
            {searchText && (
              <button
                onClick={() => { setSearchText(''); inputRef.current?.focus(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded 
                           text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors"
              >
                <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
          {searchText && (
            <div className="text-[11px] text-slate-400 mt-1.5 px-0.5">
              找到 <span className="font-semibold text-slate-600">{filteredPois.length}</span> 条
              {filteredPois.length !== pois.length && (
                <span>（共 {pois.length} 条）</span>
              )}
            </div>
          )}
        </div>

        {/* === 列表内容 === */}
        <div className="max-h-[55vh] overflow-y-auto">
          {filteredPois.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-4">
              <svg className="w-10 h-10 text-slate-300 mb-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-slate-400">未找到匹配的 POI</p>
              <button
                onClick={() => setSearchText('')}
                className="mt-2 text-xs text-blue-500 hover:text-blue-700 transition-colors"
              >
                清除筛选
              </button>
            </div>
          ) : (
            filteredPois.map((poi, index) => {
              const tags = parseTags(getPoiScene(poi));
              const numColor = getNumberColor(tags[0]);
              
              return (
                <div 
                  key={poi.gid || index}
                  className="px-4 py-3 border-b border-slate-100 hover:bg-blue-50/60 cursor-pointer transition-colors group"
                  onClick={() => onPoiClick?.(poi, index)}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5 text-white"
                      style={{ backgroundColor: numColor }}
                    >
                      {index + 1}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-slate-800 truncate group-hover:text-blue-600 transition-colors">
                          {poi.name}
                        </span>
                        <svg
                          className="w-3.5 h-3.5 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      
                      {tags.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                          {tags.map((tag, i) => (
                            <span 
                              key={i}
                              className={`px-2 py-0.5 rounded text-[10px] font-medium ${getTagStyleClass(tag)}`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {poi.addr && (
                        <div className="flex items-center gap-1 mt-1.5 text-xs text-slate-400">
                          <svg className="w-3 h-3 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          <span className="truncate">{poi.addr}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildingInfoList;
